import { useEffect, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { RootState } from 'store';
import {
	fetchUserSummaryCached,
	getCachedTeamForUsername,
	setCachedTeamForUsername,
} from 'features/salesOrder/userSummaryCache';

// Override de team para testing local — solo disponible fuera de producción.
// Uso desde la consola del browser:
//   localStorage.setItem('__dev_team__', 'WTC UY')   → activa override
//   localStorage.removeItem('__dev_team__')           → desactiva override
function getDevTeamOverride(): string | null {
	if (process.env.NODE_ENV === 'production') return null;
	return localStorage.getItem('__dev_team__');
}

export function useCurrentUserTeam(): { team: string | undefined; loading: boolean } {
	const user = useSelector((state: RootState) => state.auth.user, shallowEqual);
	const username = user?.username;
	const devOverride = getDevTeamOverride();

	const [team, setTeam] = useState<string | undefined>(() => {
		if (devOverride) return devOverride;
		if (!username) return undefined;
		const cached = getCachedTeamForUsername(username);
		return cached === null ? undefined : cached;
	});

	const [loading, setLoading] = useState(() => {
		if (devOverride) return false;
		if (!username) return false;
		return getCachedTeamForUsername(username) === null;
	});

	useEffect(() => {
		// Con override activo no hacemos fetch real
		if (devOverride) {
			setTeam(devOverride);
			setLoading(false);
			return;
		}

		if (!username) {
			setTeam(undefined);
			setLoading(false);
			return;
		}

		const cachedTeam = getCachedTeamForUsername(username);
		if (cachedTeam !== null) {
			setTeam(cachedTeam);
			setLoading(false);
		} else {
			setLoading(true);
		}

		let cancelled = false;

		fetchUserSummaryCached()
			.then((users) => {
				if (cancelled) return;
				const currentUser = users.find((entry) => entry.username === username);
				const resolvedTeam = currentUser?.team;
				setCachedTeamForUsername(username, resolvedTeam);
				setTeam(resolvedTeam);
			})
			.catch(() => {
				if (!cancelled) setTeam(undefined);
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});

		return () => {
			cancelled = true;
		};
	}, [username, devOverride]);

	return { team, loading };
}
